import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, Loader2, Settings, Clock, TrendingUp, FileText, Send, Settings2 } from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TimesheetDeadlineSettings } from '@/components/TimesheetDeadlineSettings';
import { TimesheetDataRequirementsSettings } from '@/components/TimesheetDataRequirementsSettings';
import { UtilisationSettings } from '@/components/UtilisationSettings';
import { WorkingHoursConfigurationCompact } from '@/components/settings/WorkingHoursConfigurationCompact';
import type { 
  TimesheetDeadlineSettings as DeadlineSettingsType,
  UtilisationSettings as UtilisationSettingsType,
  OrganisationTimesheetSettings,
  WorkingProfile,
  PolicyViolationSettings,
  PolicyLimits
} from '@/types/timesheetSettings';
import { toast } from 'sonner';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { ApiError } from '@/services/apiClient';
import * as settingsRepository from '@/services/settingsRepository';
import { workingPatternToProfile } from '@/services/settingsRepository';

interface SettingsPageProps {
  onNavigate?: (path: string) => void;
}

export const SettingsPage: React.FC<SettingsPageProps> = ({ onNavigate }) => {
  const { mode, isDemo, isLive } = useScreenDataMode('settings-general');
  
  const [deadlineSettings, setDeadlineSettings] = useState<DeadlineSettingsType | null>(null);
  const [utilisationSettings, setUtilisationSettings] = useState<UtilisationSettingsType | null>(null);
  const [workingProfile, setWorkingProfile] = useState<WorkingProfile | null>(null);
  const [policyEnforcement, setPolicyEnforcement] = useState<PolicyViolationSettings | null>(null);
  const [policyLimits, setPolicyLimits] = useState<PolicyLimits | undefined>(undefined);
  const [fullSettings, setFullSettings] = useState<OrganisationTimesheetSettings | null>(null);
  const [rowVersion, setRowVersion] = useState<string | undefined>(undefined);
  
  // Data requirements state
  const [weekStartDay, setWeekStartDay] = useState<0 | 1 | 2 | 3 | 4 | 5 | 6>(1);
  const [showRole, setShowRole] = useState(false);
  const [requireRole, setRequireRole] = useState(false);
  const [showCostCentre, setShowCostCentre] = useState(true);
  const [requireCostCentre, setRequireCostCentre] = useState(true);
  const [requireDescription, setRequireDescription] = useState<'never' | 'always'>('always');
  
  // Project selection state
  const [projectSelectionScope, setProjectSelectionScope] = useState<'all' | 'assigned'>('all');
  const [showArchivedProjects, setShowArchivedProjects] = useState(false);
  const [showProjectTimeRemaining, setShowProjectTimeRemaining] = useState(true);
  
  // Time entry validation state
  const [minimumEntryDuration, setMinimumEntryDuration] = useState<'6min' | '15min' | '30min' | '1hour'>('15min');
  const [timeRounding, setTimeRounding] = useState<'none' | '15min' | '30min' | '1hour'>('none');
  const [allowNegativeHours, setAllowNegativeHours] = useState(false);
  
  // Date range restrictions state
  const [historicalEntryLimit, setHistoricalEntryLimit] = useState(4);
  const [allowFutureDatedEntries, setAllowFutureDatedEntries] = useState<'no' | '1week' | '2weeks' | '4weeks' | 'unlimited'>('2weeks');
  
  // Smart Features state
  const [enableCopyPreviousWeek, setEnableCopyPreviousWeek] = useState(true);
  const [copyDescriptions, setCopyDescriptions] = useState(true);
  const [copyOnlyBillable, setCopyOnlyBillable] = useState(false);
  const [enableAutoSmartFill, setEnableAutoSmartFill] = useState(true);
  const [enableAiInsights, setEnableAiInsights] = useState(true);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('working-hours');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const handleDeadlineChange = useCallback((settings: DeadlineSettingsType, isValid: boolean) => {
    setDeadlineSettings(settings);
    setHasUnsavedChanges(true);
  }, []);

  const handleUtilisationChange = useCallback((settings: UtilisationSettingsType, isValid: boolean) => {
    setUtilisationSettings(settings);
    setHasUnsavedChanges(true);
  }, []);

  const handleWorkingProfileChange = useCallback((profile: WorkingProfile) => {
    setWorkingProfile(profile);
    setHasUnsavedChanges(true);
  }, []);

  const handlePolicyEnforcementChange = useCallback((enforcement: PolicyViolationSettings) => {
    setPolicyEnforcement(enforcement);
    setHasUnsavedChanges(true);
  }, []);

  const handlePolicyLimitsChange = useCallback((limits: PolicyLimits | undefined) => {
    setPolicyLimits(limits);
    setHasUnsavedChanges(true);
  }, []);

  // Data requirements change handlers with unsaved tracking
  const handleWeekStartDayChange = useCallback((day: 0 | 1 | 2 | 3 | 4 | 5 | 6) => {
    setWeekStartDay(day);
    setHasUnsavedChanges(true);
  }, []);

  const handleShowRoleChange = useCallback((show: boolean) => {
    setShowRole(show);
    setHasUnsavedChanges(true);
  }, []);

  const handleRequireRoleChange = useCallback((require: boolean) => {
    setRequireRole(require);
    setHasUnsavedChanges(true);
  }, []);

  const handleShowCostCentreChange = useCallback((show: boolean) => {
    setShowCostCentre(show);
    setHasUnsavedChanges(true);
  }, []);

  const handleRequireCostCentreChange = useCallback((require: boolean) => {
    setRequireCostCentre(require);
    setHasUnsavedChanges(true);
  }, []);

  const handleRequireDescriptionChange = useCallback((value: 'never' | 'always') => {
    setRequireDescription(value);
    setHasUnsavedChanges(true);
  }, []);

  // Project selection change handlers
  const handleProjectSelectionScopeChange = useCallback((scope: 'all' | 'assigned') => {
    setProjectSelectionScope(scope);
    setHasUnsavedChanges(true);
  }, []);

  const handleShowArchivedProjectsChange = useCallback((show: boolean) => {
    setShowArchivedProjects(show);
    setHasUnsavedChanges(true);
  }, []);

  const handleShowProjectTimeRemainingChange = useCallback((show: boolean) => {
    setShowProjectTimeRemaining(show);
    setHasUnsavedChanges(true);
  }, []);

  // Time entry validation change handlers
  const handleMinimumEntryDurationChange = useCallback((duration: '6min' | '15min' | '30min' | '1hour') => {
    setMinimumEntryDuration(duration);
    setHasUnsavedChanges(true);
  }, []);

  const handleTimeRoundingChange = useCallback((rounding: 'none' | '15min' | '30min' | '1hour') => {
    setTimeRounding(rounding);
    setHasUnsavedChanges(true);
  }, []);

  const handleAllowNegativeHoursChange = useCallback((allow: boolean) => {
    setAllowNegativeHours(allow);
    setHasUnsavedChanges(true);
  }, []);

  const handleHistoricalEntryLimitChange = useCallback((weeks: number) => {
    setHistoricalEntryLimit(weeks);
    setHasUnsavedChanges(true);
  }, []);

  const handleAllowFutureDatedEntriesChange = useCallback((allow: 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited') => {
    setAllowFutureDatedEntries(allow);
    setHasUnsavedChanges(true);
  }, []);

  // Smart Features change handlers
  const handleEnableCopyPreviousWeekChange = useCallback((enable: boolean) => {
    setEnableCopyPreviousWeek(enable);
    setHasUnsavedChanges(true);
  }, []);

  const handleCopyDescriptionsChange= useCallback((copy: boolean) => {
    setCopyDescriptions(copy);
    setHasUnsavedChanges(true);
  }, []);

  const handleCopyOnlyBillableChange = useCallback((copy: boolean) => {
    setCopyOnlyBillable(copy);
    setHasUnsavedChanges(true);
  }, []);

  const handleEnableAutoSmartFillChange = useCallback((enable: boolean) => {
    setEnableAutoSmartFill(enable);
    setHasUnsavedChanges(true);
  }, []);

  const handleEnableAiInsightsChange = useCallback((enable: boolean) => {
    setEnableAiInsights(enable);
    setHasUnsavedChanges(true);
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch both timesheet settings and working pattern in parallel
      const [settings, workingPattern] = await Promise.all([
        settingsRepository.getOrganisationTimesheetSettings(mode),
        settingsRepository.getOrganisationWorkingPattern(mode),
      ]);
      
      setFullSettings(settings);
      setDeadlineSettings(settings.deadline);
      setUtilisationSettings(settings.utilisation);
      
      // Load data requirements settings
      setWeekStartDay(settings.weekStartDay);
      setShowRole(settings.showRole);
      setRequireRole(settings.requireRole);
      setShowCostCentre(settings.showCostCentre);
      setRequireCostCentre(settings.requireCostCentre);
      
      // Load smart features settings from deadline settings
      setEnableCopyPreviousWeek(settings.deadline.enableCopyPreviousWeek);
      setCopyDescriptions(settings.deadline.copyDescriptions);
      setCopyOnlyBillable(settings.deadline.copyOnlyBillable);
      setEnableAutoSmartFill(settings.deadline.enableAutoSmartFill ?? true);
      setEnableAiInsights(settings.deadline.enableAiInsights ?? true);
      
      // Load new data requirements fields from settings
      setRequireDescription(settings.requireDescription ?? 'always');
      setMinimumEntryDuration(settings.minimumEntryDuration ?? '6min');
      setTimeRounding(settings.timeRounding ?? 'none');
      setAllowNegativeHours(settings.allowNegativeHours ?? false);
      setHistoricalEntryLimit(settings.historicalEntryLimit ?? 4);
      setAllowFutureDatedEntries(settings.allowFutureDatedEntries ?? '2weeks');
      
      // Reset unsaved changes flag after loading
      setHasUnsavedChanges(false);
      
      // Convert WorkingPattern from API to WorkingProfile format for the UI
      // This ensures we read from the same source (OrganizationWorkingPattern) that we write to
      const loadedWorkingProfile = workingPatternToProfile(workingPattern);
      setWorkingProfile(loadedWorkingProfile);
      
      setPolicyEnforcement(settings.policyViolations || {
        monitoringFrequency: 'week',
        suppressDailyWhenWeeklyExceeded: true,
        consolidateDailyWarnings: true,
        checks: {
          'daily-overage': { enabled: true, threshold: 1.25, severity: 'warning' },
          'weekly-overage': { enabled: true, threshold: 1.10, severity: 'warning' },
          'legal-daily-maximum': { enabled: false, severity: 'critical' },
          'legal-weekly-maximum': { enabled: false, severity: 'critical' },
          'missing-hours': { enabled: true, threshold: 0.80, severity: 'info' },
          'non-working-day': { enabled: true, severity: 'info' }
        },
        displayMode: 'smart',
        showInfoAlerts: true,
        preset: 'balanced'
      });
      setPolicyLimits(settings.policyLimits);
      
      if ('rowVersion' in settings) {
        setRowVersion(settings.rowVersion);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to load settings. Please try again.';
      setError(errorMessage);
      
      if (isLive) {
        toast.error('Failed to load settings', {
          description: 'Could not connect to API. Please ensure the backend is running or switch to demo mode.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [mode]);

  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const ensureLiveSettings = async (): Promise<{ settings: OrganisationTimesheetSettings; version: string } | null> => {
    if (fullSettings && rowVersion !== undefined) {
      return { settings: fullSettings, version: rowVersion };
    }
    try {
      const freshSettings = await settingsRepository.getOrganisationTimesheetSettings(mode);
      setFullSettings(freshSettings);
      const freshVersion = ('rowVersion' in freshSettings) ? (freshSettings.rowVersion ?? '') : undefined;
      if (freshVersion !== undefined) {
        setRowVersion(freshVersion);
        return { settings: freshSettings, version: freshVersion };
      }
    } catch {
      // fall through
    }
    toast.error('Cannot save', {
      description: 'Could not retrieve settings version from the API. Please reload the page.',
    });
    return null;
  };

  const handleSaveUtilisation = async (settings: UtilisationSettingsType) => {
    setIsSaving(true);
    
    try {
      let currentSettings = fullSettings || undefined;
      let currentRowVersion = rowVersion;
      if (isLive) {
        const loaded = await ensureLiveSettings();
        if (!loaded) { setIsSaving(false); return; }
        currentSettings = loaded.settings;
        currentRowVersion = loaded.version;
      }
      const updatedSettings = await settingsRepository.updateUtilisationSettings(
        settings,
        currentSettings,
        currentRowVersion,
        mode
      );
      
      setUtilisationSettings(settings);
      setFullSettings(updatedSettings);
      
      if ('rowVersion' in updatedSettings) {
        setRowVersion(updatedSettings.rowVersion);
      }
      
      setHasUnsavedChanges(false);
      
      toast.success('Settings saved successfully', {
        description: 'Your changes have been applied',
      });
    } catch (err) {
      console.error('Error saving utilisation settings:', err);
      
      if (err instanceof ApiError && err.status === 409) {
        toast.error('Settings conflict', {
          description: 'Settings were modified by another user. Reloading...',
        });
        await loadData();
      } else {
        toast.error('Failed to save settings', {
          description: err instanceof ApiError ? err.message : 'Please try again',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveWorkingHoursSettings = async () => {
    if (!workingProfile || !policyEnforcement) return;
    
    setIsSaving(true);
    
    try {
      let currentSettings = fullSettings || undefined;
      let currentRowVersion = rowVersion;
      if (isLive) {
        const loaded = await ensureLiveSettings();
        if (!loaded) { setIsSaving(false); return; }
        currentSettings = loaded.settings;
        currentRowVersion = loaded.version;
      }
      const updatedSettings = await settingsRepository.updateWorkingHoursSettings(
        workingProfile,
        policyEnforcement,
        policyLimits,
        currentSettings,
        currentRowVersion,
        mode
      );
      
      setWorkingProfile(workingProfile);
      setPolicyEnforcement(policyEnforcement);
      setPolicyLimits(policyLimits);
      setFullSettings(updatedSettings);
      
      if ('rowVersion' in updatedSettings) {
        setRowVersion(updatedSettings.rowVersion);
      }
      
      setHasUnsavedChanges(false);
      
      toast.success('Settings saved successfully', {
        description: 'Your changes have been applied',
      });
    } catch (err) {
      console.error('Error saving working hours settings:', err);
      
      if (err instanceof ApiError && err.status === 409) {
        toast.error('Settings conflict', {
          description: 'Settings were modified by another user. Reloading...',
        });
        await loadData();
      } else {
        toast.error('Failed to save settings', {
          description: err instanceof ApiError ? err.message : 'Please try again',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Save handler for Submissions & Deadlines tab (deadline settings only)
  const handleSaveGeneralSettings = async () => {
    if (!deadlineSettings) return;
    
    setIsSaving(true);
    
    try {
      let currentSettings = fullSettings || undefined;
      let currentRowVersion = rowVersion;
      if (isLive) {
        const loaded = await ensureLiveSettings();
        if (!loaded) { setIsSaving(false); return; }
        currentSettings = loaded.settings;
        currentRowVersion = loaded.version;
      }
      const updatedSettings = await settingsRepository.updateDeadlineSettings(
        deadlineSettings,
        currentSettings,
        currentRowVersion,
        mode
      );
      
      setFullSettings(updatedSettings);
      
      if ('rowVersion' in updatedSettings) {
        setRowVersion(updatedSettings.rowVersion);
      }
      
      setHasUnsavedChanges(false);
      
      toast.success('Settings saved successfully', {
        description: 'Your changes have been applied',
      });
    } catch (err) {
      console.error('Error saving deadline settings:', err);
      
      if (err instanceof ApiError && err.status === 409) {
        toast.error('Settings conflict', {
          description: 'Settings were modified by another user. Reloading...',
        });
        await loadData();
      } else {
        toast.error('Failed to save settings', {
          description: err instanceof ApiError ? err.message : 'Please try again',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Save handler for General tab (data requirements only)
  const handleSaveDataRequirementsSettings = async () => {
    setIsSaving(true);
    
    try {
      let currentSettings = fullSettings || undefined;
      let currentRowVersion = rowVersion;
      if (isLive) {
        const loaded = await ensureLiveSettings();
        if (!loaded) { setIsSaving(false); return; }
        currentSettings = loaded.settings;
        currentRowVersion = loaded.version;
      }
      
      const updatedDeadlineSettings = {
        ...deadlineSettings!,
        enableCopyPreviousWeek,
        copyDescriptions,
        copyOnlyBillable,
        enableAutoSmartFill,
        enableAiInsights,
      };
      
      const mergedSettings: OrganisationTimesheetSettings = {
        ...(currentSettings || {} as OrganisationTimesheetSettings),
        weekStartDay,
        showRole,
        requireRole,
        showCostCentre,
        requireCostCentre,
        requireDescription,
        minimumEntryDuration,
        timeRounding,
        allowNegativeHours,
        historicalEntryLimit,
        allowFutureDatedEntries,
        deadline: updatedDeadlineSettings,
      };
      
      const updatedSettings = await settingsRepository.updateOrganisationTimesheetSettings(
        mergedSettings,
        currentRowVersion,
        mode
      );
      
      setFullSettings(updatedSettings);
      setDeadlineSettings(updatedDeadlineSettings);
      
      if ('rowVersion' in updatedSettings && updatedSettings.rowVersion) {
        setRowVersion(updatedSettings.rowVersion);
      }
      
      setHasUnsavedChanges(false);
      
      // Save smart features to localStorage for role permissions access
      localStorage.setItem('globalTimesheetSettings', JSON.stringify({
        enableCopyPreviousWeek,
        copyDescriptions,
        copyOnlyBillable,
        enableAutoSmartFill,
        enableAiInsights,
      }));
      
      toast.success('Settings saved successfully', {
        description: 'Your changes have been applied',
      });
    } catch (err) {
      console.error('Error saving data requirements:', err);
      
      if (err instanceof ApiError && err.status === 409) {
        toast.error('Settings conflict', {
          description: 'Settings were modified by another user. Reloading...',
        });
        await loadData();
      } else {
        toast.error('Failed to save settings', {
          description: err instanceof ApiError ? err.message : 'Please try again',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };



  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="General Settings"
          subtitle="Configure organisation-wide defaults and preferences"
          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Settings2 />}
        />
        <div className="flex items-center justify-center p-12">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="General Settings"
          subtitle="Configure organisation-wide defaults and preferences"
          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Settings2 />}
        />
        <Alert variant="destructive">
          <AlertDescription>
            <p className="font-medium mb-2">{error}</p>
            <Button onClick={loadData} variant="outline" size="sm">
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!deadlineSettings || !utilisationSettings || !workingProfile || !policyEnforcement) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="General Settings"
          subtitle="Configure organisation-wide defaults and preferences"
          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Settings2 />}
        />
        <Alert variant="destructive">
          <AlertDescription>
            <p className="font-medium mb-2">Unable to load settings data</p>
            <p className="text-sm text-muted-foreground mb-3">
              Some required data is missing. This might be a data initialization issue.
            </p>
            <Button onClick={loadData} variant="outline" size="sm">
              Reload Settings
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const handleCancel = () => {
    console.log('Cancel clicked, hasUnsavedChanges:', hasUnsavedChanges);
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      toast.info('No unsaved changes to discard');
    }
  };

  const handleConfirmCancel = async () => {
    setShowCancelDialog(false);
    await loadData();
    setHasUnsavedChanges(false);
    toast.success('Changes discarded');
  };

  const handleSave = () => {
    switch (activeTab) {
      case 'general':
        handleSaveDataRequirementsSettings();
        break;
      case 'working-hours':
        handleSaveWorkingHoursSettings();
        break;
      case 'submissions-deadlines':
        handleSaveGeneralSettings();
        break;
      case 'utilisation':
        if (utilisationSettings) {
          handleSaveUtilisation(utilisationSettings);
        }
        break;
    }
  };

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <PageHeader
        title="General Settings"
        subtitle="Configure organisation-wide defaults and preferences"
        size={36}
        showActions={false}
        showBreadcrumb={false}
        icon={<Settings2 />}
      />

      {/* Tabbed Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="working-hours" className="gap-2">
            <Clock className="h-4 w-4" />
            Working Hours & Policies
          </TabsTrigger>
          <TabsTrigger value="submissions-deadlines" className="gap-2">
            <Send className="h-4 w-4" />
            Submissions & Deadlines
          </TabsTrigger>
          <TabsTrigger value="general" className="gap-2">
            <FileText className="h-4 w-4" />
            Time Entry
          </TabsTrigger>
          <TabsTrigger value="utilisation" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            Utilisation
          </TabsTrigger>
        </TabsList>

        {/* Time Entry - Data Requirements */}
        <TabsContent value="general" className="space-y-6">
          {/* Unsaved changes indicator */}
          {hasUnsavedChanges && (
            <Alert variant="warning">
              <AlertDescription>
                You have unsaved changes. Click "Save Settings" at the bottom to apply your changes.
              </AlertDescription>
            </Alert>
          )}

          <TimesheetDataRequirementsSettings
            weekStartDay={weekStartDay}
            showRole={showRole}
            requireRole={requireRole}
            showCostCentre={showCostCentre}
            requireCostCentre={requireCostCentre}
            projectSelectionScope={projectSelectionScope}
            showArchivedProjects={showArchivedProjects}
            showProjectTimeRemaining={showProjectTimeRemaining}
            minimumEntryDuration={minimumEntryDuration}
            timeRounding={timeRounding}
            allowNegativeHours={allowNegativeHours}
            historicalEntryLimit={historicalEntryLimit}
            allowFutureDatedEntries={allowFutureDatedEntries}
            enableCopyPreviousWeek={enableCopyPreviousWeek}
            copyDescriptions={copyDescriptions}
            copyOnlyBillable={copyOnlyBillable}
            enableAutoSmartFill={enableAutoSmartFill}
            enableAiInsights={enableAiInsights}
            onWeekStartDayChange={handleWeekStartDayChange}
            onShowRoleChange={handleShowRoleChange}
            onRequireRoleChange={handleRequireRoleChange}
            onShowCostCentreChange={handleShowCostCentreChange}
            onRequireCostCentreChange={handleRequireCostCentreChange}
            requireDescription={requireDescription}
            onRequireDescriptionChange={handleRequireDescriptionChange}
            onProjectSelectionScopeChange={handleProjectSelectionScopeChange}
            onShowArchivedProjectsChange={handleShowArchivedProjectsChange}
            onShowProjectTimeRemainingChange={handleShowProjectTimeRemainingChange}
            onMinimumEntryDurationChange={handleMinimumEntryDurationChange}
            onTimeRoundingChange={handleTimeRoundingChange}
            onAllowNegativeHoursChange={handleAllowNegativeHoursChange}
            onHistoricalEntryLimitChange={handleHistoricalEntryLimitChange}
            onAllowFutureDatedEntriesChange={handleAllowFutureDatedEntriesChange}
            onEnableCopyPreviousWeekChange={handleEnableCopyPreviousWeekChange}
            onCopyDescriptionsChange={handleCopyDescriptionsChange}
            onCopyOnlyBillableChange={handleCopyOnlyBillableChange}
            onEnableAutoSmartFillChange={handleEnableAutoSmartFillChange}
            onEnableAiInsightsChange={handleEnableAiInsightsChange}
          />

        </TabsContent>

        {/* Working Hours & Policies */}
        <TabsContent value="working-hours" className="space-y-6">
          {/* Unsaved changes indicator */}
          {hasUnsavedChanges && (
            <Alert variant="warning">
              <AlertDescription>
                You have unsaved changes. Click "Save Settings" to apply your changes.
              </AlertDescription>
            </Alert>
          )}

          <WorkingHoursConfigurationCompact
            workingProfile={workingProfile}
            policyEnforcement={policyEnforcement}
            policyLimits={policyLimits}
            onWorkingProfileChange={handleWorkingProfileChange}
            onPolicyEnforcementChange={handlePolicyEnforcementChange}
            onPolicyLimitsChange={handlePolicyLimitsChange}
          />
        </TabsContent>

        {/* Submissions & Deadlines */}
        <TabsContent value="submissions-deadlines" className="space-y-6">
          {/* Unsaved changes indicator */}
          {hasUnsavedChanges && (
            <Alert variant="warning">
              <AlertDescription>
                You have unsaved changes. Click "Save Settings" at the bottom to apply your changes.
              </AlertDescription>
            </Alert>
          )}

          <TimesheetDeadlineSettings
            settings={deadlineSettings}
            onChange={handleDeadlineChange}
            onNavigateToNotifications={() => onNavigate?.('/settings/notifications')}
          />
        </TabsContent>



        {/* Utilisation */}
        <TabsContent value="utilisation" className="space-y-6">
          {/* Unsaved changes indicator */}
          {hasUnsavedChanges && (
            <Alert variant="warning">
              <AlertDescription>
                You have unsaved changes. Click "Save Settings" to apply your changes.
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Utilisation & Performance</CardTitle>
              <CardDescription>
                Configure utilisation tracking and performance metrics
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <UtilisationSettings
                settings={utilisationSettings}
                onChange={handleUtilisationChange}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Sticky Footer */}
      <div className="sticky bottom-0 z-10 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 mt-8 -mx-2 sm:-mx-3 lg:-mx-3 xl:-mx-4 px-2 sm:px-3 lg:px-3 xl:px-4">
        <div className="flex h-16 items-center justify-end gap-4 max-w-[2000px] mx-auto">
          <Button 
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
            size="lg"
            className="min-w-[120px]"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={isSaving || !hasUnsavedChanges}
            size="lg"
            className="min-w-[120px]"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to discard them? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
